import RoutesEnum from '~/src/router/enums/routes.enum'
import Params from '~/src/router/enums/params.enum'
import ResponsiveDialogProps from '~/src/types/ResponsiveDialogProps'
import DeliveryAddress from '~/src/components/ordering-methods/DeliveryAddress'
import useValidateOrder, { OrderErrorType } from '~/src/hooks/orders/useValidateOrder'
import { Constants } from '~/src/helpers/constants'
import { useContext } from 'react'
import ResponsiveDialogContext, {
  ResponsiveDialogContextInterface
} from '~/src/components/mui-wrappers/responsive-dialog/ResponsiveDialogContext'
import { useRouterPush } from '~/src/hooks/router/useRouterPush'
import { useFirmPathResolver } from '~/src/hooks/router/useFirmPathResolver'
import { useEoValue } from '~/src/hooks/useEoState'
import { IS_LOGGED_IN_AS_SELECTOR_FAMILY } from '~/src/stores/authentication'
import useGuestFormFields from '~/src/hooks/forms/guest-form/useGuestFormFields'
import useDialog from '~/src/hooks/useDialog'
import useCustomerExtraInfo from '~/src/hooks/customer/useCustomerExtraInfo'
import { Customer } from '@eo-storefronts/eo-core'
import useAuthenticationFlowDialogs from '~/src/hooks/auth/flow/useAuthenticationFlowDialogs'
import { GuestMissingFieldsDialogProps } from '~/src/components/auth/dialogs/missing-fields/GuestMissingFieldsDialog'
import { AuthenticationFlowDialogNameEnum, DialogNameEnum } from '~/src/types/DialogNameEnum'
import { useLocation } from 'react-router-dom'
import {
  CustomerMissingFieldsDialogProps
} from '~/src/components/auth/dialogs/missing-fields/CustomerMissingFieldsDialog'

interface ReturnsType {
  finalize(): void,
  continueAsCustomer(): void,
  continueAsGuest(): void,
}

const useShoppingCartActions = (): ReturnsType => {
  const { pathname } = useLocation()
  const { push } = useRouterPush()
  const { resolve } = useFirmPathResolver()
  const { getErrors } = useValidateOrder()
  const { restart, goToStep } = useAuthenticationFlowDialogs()
  const isLoggedInAsGuest: boolean = useEoValue(IS_LOGGED_IN_AS_SELECTOR_FAMILY('guest'))
  const isLoggedInAsCustomer: boolean = useEoValue(IS_LOGGED_IN_AS_SELECTOR_FAMILY('customer'))
  const responsiveDialogContext = useContext<ResponsiveDialogContextInterface>(ResponsiveDialogContext)
  const { present } = useDialog()
  const { areAllHidden: guestFormFieldsAreAllHidden } = useGuestFormFields()
  const { needsExtraInfo } = useCustomerExtraInfo()

  const _goToCheckout = (customer?: Customer) => {
    responsiveDialogContext?.toggleClose && responsiveDialogContext.toggleClose()

    if (isLoggedInAsGuest && !customer && !guestFormFieldsAreAllHidden()) {
      push(`${pathname}?${Params.REDIRECT_TO}=CHECKOUT`)

      return goToStep<GuestMissingFieldsDialogProps>(
        AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_GUEST_MISSING_FIELDS,
        {
          showBackButton: false
        }
      )
    }

    if ((isLoggedInAsCustomer || customer) && needsExtraInfo(customer)) {
      push(`${pathname}?${Params.REDIRECT_TO}=CHECKOUT`)

      return goToStep<CustomerMissingFieldsDialogProps>(
        AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_CUSTOMER_MISSING_FIELDS,
        {
          showBackButton: false
        }
      )
    }

    push(resolve(RoutesEnum.CHECKOUT))
  }

  const _presentDeliveryAddressDialog = () => {
    present<ResponsiveDialogProps>(DeliveryAddress, DialogNameEnum.DELIVERY_ADDRESS, {
      openByDefault: true,
      activator: undefined
    })
  }

  const _check = (customer?: Customer) => {
    if (getErrors().find((e: OrderErrorType) => e.type === 'address')) {
      _presentDeliveryAddressDialog()
    } else {
      _goToCheckout(customer)
    }
  }

  const finalize = () => {
    setTimeout(() => {
      _check()
    }, Constants.DEBOUNCE_TIME)
  }

  const continueAsGuest = () => {
    _check()
  }

  const continueAsCustomer = () => {
    push(`${pathname}?${Params.REDIRECT_TO}=CHECKOUT`)

    return restart()
  }

  return {
    finalize,
    continueAsCustomer,
    continueAsGuest
  }
}

export default useShoppingCartActions
